import {
  GenericError,
  InitiateUploadParams,
  MediaObjectUpload,
  uploadMedia,
} from '@kudos/http-client'
import { useMediaUpload } from '@shared/hooks/useMediaUpload'
import { useMutation } from '@tanstack/react-query'
import { useT } from '@transifex/react'
import { AxiosError } from 'axios'
import { useFormContext } from 'react-hook-form'
import { RichTextEditor } from './RichTextEditorField'

export type ImageUploadFn = (
  params: InitiateUploadParams,
  isInline?: boolean,
) => Promise<MediaObjectUpload>

const useRichTextImageUpload = (initiateUploadFn?: ImageUploadFn) => {
  const { t } = useT()
  const { clearErrors, setError } = useFormContext()
  const { handleMediaUpload } = useMediaUpload()
  const uploadMutation = useMutation({
    mutationFn: ({ url, data }: { url: string; data: FormData }) => uploadMedia({ url, data }),
  })

  const initiateUploadMutation = useMutation({
    mutationFn: ({
      originalFilename,
      contentType,
    }: {
      originalFilename: string
      contentType: string
    }) => {
      if (!initiateUploadFn) {
        throw new Error('Image upload function is not provided')
      }
      return initiateUploadFn({ originalFilename, contentType }, true)
    },
  })

  const upload = async (img: File, editor: RichTextEditor, name: string) => {
    if (!initiateUploadFn) {
      return
    }
    clearErrors(name)
    try {
      const initiateUploadResponse = await initiateUploadMutation.mutateAsync({
        originalFilename: img.name || 'attachmentUrl',
        contentType: img.type,
      })

      const { awsFormData, uploadUrl, mediaObjectUrl } = handleMediaUpload({
        initiateUploadResponse,
        file: img,
      })

      await uploadMutation.mutateAsync({
        url: uploadUrl,
        data: awsFormData,
      })

      editor.image.insert(mediaObjectUrl, null, null, editor.image.get())
    } catch (error) {
      const axiosError = error as AxiosError
      const axiosErrorData = axiosError.response?.data as unknown as GenericError
      setError(name, { message: axiosErrorData.msg || t('Failed to upload image') })
    }
  }

  return { upload }
}

export default useRichTextImageUpload
